import { Box, Tabs, Tab, Tooltip, Typography, Divider, type SxProps } from '@mui/material';
import { useState, useTransition, type JSX } from 'react';

const DividerWrapper = ({ children }: { children: React.ReactNode }): React.ReactNode => children;

export type HealtheraTab = {
  label: string;
  value: string;
  tooltip?: string;
  disabled?: boolean;
  count?: number;
  badge?: string;
};

export type HealtheraTabsProps = {
  tabs: HealtheraTab[];
  selectedTab: string;
  handleChange: (event: React.SyntheticEvent, newValue: string) => void;
  dividerAfterIndex?: number;
  sx?: SxProps;
};

export default function HealtheraTabs({
  tabs,
  selectedTab,
  handleChange,
  dividerAfterIndex,
  sx,
}: HealtheraTabsProps): JSX.Element {
  const [isPending, startTransition] = useTransition();
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const onChange = (event: React.SyntheticEvent, newValue: string): void => {
    setPendingTab(newValue);
    startTransition(() => {
      handleChange(event, newValue);
    });
  };

  const activeTab = isPending && pendingTab !== null ? pendingTab : selectedTab;

  return (
    <Box className="w-full relative border-b border-gray-200" aria-busy={isPending}>
      <Tabs value={activeTab || false} onChange={onChange} aria-label="Services page tabs" sx={sx}>
        {tabs.map((tab, idx) => [
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <TooltipWrapper tooltip={tab.tooltip}>
                <Label tab={tab} />
              </TooltipWrapper>
            }
            disabled={tab.disabled}
          />,
          dividerAfterIndex === idx ? (
            <DividerWrapper key={`divider-${idx}`}>
              <Divider orientation="vertical" flexItem className="mx-4" />
            </DividerWrapper>
          ) : null,
        ])}
      </Tabs>
    </Box>
  );
}

function Label({ tab }: { tab: HealtheraTab }): JSX.Element {
  const badgeStyle =
    tab.badge === 'New'
      ? {
          background: 'linear-gradient(90deg, #4DB181 0%, #3F7284 100%)',
          color: '#FFFFFF',
        }
      : {};
  const badgeClassNames = tab.badge === 'New' ? '' : 'bg-typography-danger';

  return (
    <Box className="flex items-center gap-8">
      <Typography className="font-500">{tab.label}</Typography>
      {typeof tab.count === 'number' && (
        <Typography className="font-500">{`(${tab.count})`}</Typography>
      )}
      {tab.badge && (
        <Box className={`${badgeClassNames} px-8 h-20 rounded-full`} style={badgeStyle}>
          <Typography className="text-white text-12 font-500 leading-[20px]">
            {tab.badge}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function TooltipWrapper({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip?: string;
}): JSX.Element | React.ReactNode {
  if (!tooltip) return children;

  return (
    <Tooltip title={tooltip} arrow>
      <Box>{children}</Box>
    </Tooltip>
  );
}
